import { FC } from 'react';

import { Link } from '@inertiajs/react';
import { CheckCircle, MessageSquare, PackageCheck, Store } from 'lucide-react';

import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Seller, ShippingMethod } from '../../types';
import {
    formatReviewScore,
    getProductsCountText,
    getReviewsCountText,
    getSalesCountText,
} from '../../utils';

interface Props {
    seller: Seller;
}

const SHIPPING_METHOD_LABELS: Record<ShippingMethod, string> = {
    nova_poshta: 'Нова пошта',
    ukrposhta: 'Укрпошта',
    pickup: 'Самовивіз',
};

export const SellerCard: FC<Props> = ({ seller }) => {
    const shippingMethods = seller.shipping_methods
        .map((method) => SHIPPING_METHOD_LABELS[method])
        .filter(Boolean);

    return (
        <Card
            className="flex h-full max-w-full min-w-0 flex-col overflow-hidden rounded-2xl border-border/70 transition-colors hover:border-primary/40"
            data-testid={`seller-card-${seller.id}`}
        >
            <CardContent className="flex h-full min-w-0 flex-col gap-5 p-5">
                <div className="flex min-w-0 items-start gap-4">
                    <Avatar className="h-16 w-16 shrink-0 border-2 border-background shadow-sm">
                        <AvatarImage
                            src={seller.avatar}
                            alt={seller.nickname}
                        />
                        <AvatarFallback className="text-lg font-semibold">
                            {seller.nickname.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                        <Link
                            href={seller.url_path}
                            className="text-lg font-semibold break-words hover:text-primary"
                        >
                            {seller.nickname}
                        </Link>

                        {seller.bio && (
                            <p className="mt-1 line-clamp-2 text-sm leading-5 text-muted-foreground">
                                {seller.bio}
                            </p>
                        )}
                    </div>
                </div>

                <div className="grid min-w-0 grid-cols-2 gap-3 text-sm">
                    <div className="min-w-0 rounded-xl bg-muted/60 p-3">
                        <Store className="mb-2 h-4 w-4 text-primary" />
                        <p className="font-medium">
                            {getProductsCountText(seller.offers_count)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            активні пропозиції
                        </p>
                    </div>

                    <div className="min-w-0 rounded-xl bg-muted/60 p-3">
                        <CheckCircle className="mb-2 h-4 w-4 text-green-600" />
                        <p className="font-medium">
                            {getSalesCountText(seller.completed_sales_count)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            завершено
                        </p>
                    </div>
                </div>

                <div className="flex min-h-6 max-w-full min-w-0 flex-wrap items-center gap-2">
                    {seller.reviews_count > 0 && (
                        <Badge
                            variant="outline"
                            className="max-w-full min-w-0 items-start gap-1.5 whitespace-normal"
                        >
                            <MessageSquare className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                            <span className="min-w-0 break-words">
                                {formatReviewScore(
                                    seller.average_overall_rating_score,
                                )}
                                /5 · {getReviewsCountText(seller.reviews_count)}
                            </span>
                        </Badge>
                    )}

                    {shippingMethods.length > 0 && (
                        <Badge
                            variant="neutral"
                            className="max-w-full min-w-0 items-start gap-1.5 whitespace-normal"
                        >
                            <PackageCheck className="h-3.5 w-3.5 shrink-0" />
                            <span className="min-w-0 break-words">
                                {shippingMethods.join(' · ')}
                            </span>
                        </Badge>
                    )}
                </div>

                <Button asChild className="mt-auto w-full">
                    <Link href={seller.url_path}>Переглянути товари</Link>
                </Button>
            </CardContent>
        </Card>
    );
};
